import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { type TabWithContent } from "@/components/TabsWithNavigation";
import { useRoute } from "@/routes/getRoute";

export interface UseTabNavigationConfig {
  routes: TabWithContent[];
  title: string;
  onSaveDraft?: () => void;
  onSubmit?: () => void;
  initialHasUnsavedChanges?: boolean;
  completionMode?: "navigation" | "content";
  initialCompletedTabs?: string[];
}

export const useTabNavigation = ({
  routes,
  title,
  onSaveDraft,
  onSubmit,
  initialHasUnsavedChanges = false,
  completionMode,
  initialCompletedTabs = [],
}: UseTabNavigationConfig) => {
  const location = useLocation();
  const getRoute = useRoute();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(
    initialHasUnsavedChanges,
  );
  const [completedTabs, setCompletedTabs] = useState<Set<string>>(
    new Set(initialCompletedTabs),
  );

  const getCurrentTabId = useCallback(() => {
    const currentPath = location.pathname;

    const currentTab = routes.find((route) => {
      if (route.route) {
        const expectedPath = getRoute(route.route, route.params || {});
        return (
          currentPath === expectedPath ||
          currentPath.includes(expectedPath.split("/").pop() || "")
        );
      }
      return false;
    });

    return currentTab?.id || "";
  }, [routes, location.pathname, getRoute]);

  const currentTabId = getCurrentTabId();

  const markTabCompleted = useCallback((tabId: string) => {
    setCompletedTabs((prev) => new Set([...prev, tabId]));
  }, []);

  const markTabIncomplete = useCallback((tabId: string) => {
    setCompletedTabs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(tabId);
      return newSet;
    });
  }, []);

  const isTabCompleted = useCallback(
    (tabId: string) => {
      return completedTabs.has(tabId);
    },
    [completedTabs],
  );

  const handleTabChange = useCallback(
    (newTabId: string) => {
      if (completionMode === "content") {
        return;
      }

      const effectiveCurrentTabId = currentTabId || routes[0]?.id || "";
      const currentTabIndex = routes.findIndex(
        (route) => route.id === effectiveCurrentTabId,
      );
      const newTabIndex = routes.findIndex((route) => route.id === newTabId);

      if (currentTabIndex === -1 || newTabIndex === -1) return;

      if (newTabIndex > currentTabIndex) {
        setCompletedTabs((prev) => new Set([...prev, effectiveCurrentTabId]));
      }

      if (newTabIndex < currentTabIndex) {
        setCompletedTabs((prev) => {
          const newSet = new Set(prev);
          newSet.delete(effectiveCurrentTabId);
          return newSet;
        });
      }

      setCompletedTabs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(newTabId);
        return newSet;
      });
    },
    [currentTabId, routes, completionMode],
  );

  const handleSaveDraft = useCallback(() => {
    onSaveDraft?.();
    setHasUnsavedChanges(false);
  }, [onSaveDraft]);

  const handleSubmit = useCallback(() => {
    onSubmit?.();
    setHasUnsavedChanges(false);
  }, [onSubmit]);

  return {
    routes,
    title,
    currentTabId,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    completedTabs,
    markTabCompleted,
    markTabIncomplete,
    isTabCompleted,
    handleTabChange,
    handleSaveDraft,
    handleSubmit,
  };
};
